import { HttpParams } from '@angular/common/http';
import { AsyncValidatorFn, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Column } from '@orion-platform/datagrid';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { EMPTY, map, Observable, OperatorFunction, switchMap, take } from 'rxjs';
import { DashboardPanelModel } from '../models/sv-dashboard';
import { PermissionKey } from '../pages/contacts/contacts.types';
import {
  AddUserSettings,
  EditUserSettings,
} from '../store/supply-visibility/supply-visibility.actions';
import { FieldVisibilitySetting } from '../models/supply-visibility.model';
import { PasteComputeInput, PasteComputeResult, Plants } from '../types';

export type FormValue = {
  asyncValidators?: AsyncValidatorFn | AsyncValidatorFn[] | null;
  validators?: null | ValidatorFn | ValidatorFn[];
  value: unknown;
};

export type UnwrapValueForm<T extends Record<string, FormValue>> = {
  [K in keyof T]: T[K]['value'];
};

/**
 * Compares two objects for equality.
 * @template T
 * @param {T} a - The original object to compare.
 * @param {T} b - The updated object to compare.
 * @returns {boolean} `true` if both objects are equal; otherwise `false`.
 */
export const sameValues = <T extends Record<string, unknown>>(a: T, b: T): boolean => {
  const ak = Object.keys(a) as Array<keyof T>;
  const bk = Object.keys(b) as Array<keyof T>;

  if (ak.length !== bk.length) return false;

  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!Object.is(a[k], b[k])) return false;
  }

  return true;
};

export const generateHttpParams = (
  obj: Record<string, boolean | null | number | string | undefined>,
): HttpParams => {
  let p = new HttpParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined && v !== '') p = p.set(k, String(v));
  }
  return p;
};

export const formatParams = (data?: ApiQueryParams): HttpParams => {
  return generateHttpParams({
    $orderby: data?.sort as string | undefined,
    $skip: data?.skip,
    $top: data?.top,
    searchText: data?.searchValue,
  });
};

export const getUrl = (baseUrl: string, route: string): string => {
  const base = baseUrl.replace(/\/+$/, '');
  const r = String(route ?? '').replace(/^\/+/, '');
  return `${base}/${r}`;
};

export const isMulti = (plant: Plants): boolean =>
  plant === Plants.MULTI || String(plant).toUpperCase() === Plants.MULTI;

/**
 * Merge widget config values into the default edit form, immutably.
 * - For every key in `defaultForm`, if `config` provides a defined value for that key,
 *   the field’s `value` is replaced with the incoming value.
 * - Keys not present (or `undefined`) in `config` keep their default `value`.
 * @param {T} defaultForm
 *   The base form object (each field is expected to be an object containing at least a `value` property).
 * @param {DashboardPanelModel} config
 *   Source of incoming values; only matching keys are considered.
 * @returns {T}
 *   A new form object with merged `value`s.
 */
export const setFormValues = <T extends Record<string, unknown>>(
  defaultForm: T,
  config: DashboardPanelModel,
): T => {
  return (Object.keys(defaultForm) as Array<keyof T>).reduce<T>(
    (acc, key): T => {
      const def = defaultForm[key] as Record<string, unknown>;
      const incoming = config[key as keyof DashboardPanelModel];

      acc[key] = (incoming ? { ...def, value: incoming } : def) as T[keyof T];

      return acc;
    },
    { ...defaultForm },
  );
};

/**
 * What a column looks like once it is stored.
 *
 * Only what is read back: the arrangement is the user's, the rest of a column
 * — its caption, its type, how it renders — belongs to the code and is rebuilt
 * from there on every load (see `reconcileColumns` and
 * `Helper.formatColumnsWithUserSettings`). Storing more would mean a caption
 * written in one user's language sitting in a record everyone reads, and a
 * renamed column keeping its old name forever.
 * @param {Column[]} columns The columns in the order the user arranged them.
 * @returns {FieldVisibilitySetting[]} The part of them worth keeping.
 */
export const toStoredColumns = (columns: Column[]): FieldVisibilitySetting[] =>
  columns.map((column, index): FieldVisibilitySetting => ({
    field: String(column.field),
    index,
    visible: Boolean(column.visible),
  }));

/**
 * Builds a user-settings action to store columns settings to BE.
 * @param hasDefault - Checks if user is still using default columns. If `true`, returns an {@link AddUserSettings} action; otherwise returns an {@link EditUserSettings} action.
 * @param columns - The ordered list of columns to persist..
 * @returns {AddUserSettings | EditUserSettings} An action instance ready to be dispatched.
 */
export const buildSettingsAction = (
  hasDefault: boolean,
  columns: Column[],
  key: string,
): AddUserSettings | EditUserSettings => {
  const actionPayload = {
    data: JSON.stringify(toStoredColumns(columns)),
    key,
  };

  return hasDefault ? new AddUserSettings(actionPayload) : new EditUserSettings(actionPayload);
};

/**
 * Creates an Angular {@link FormGroup} from a plain object of default values.
 * Each own property of `defaults` becomes a {@link FormControl} initialized with
 * the corresponding value. The resulting group's control names mirror the keys
 * of `defaults`.
 * @typeParam T - The shape of the form's value object.
 * @param defaults - A plain object containing initial values for each control.
 * @returns A {@link FormGroup} whose controls are created from `defaults`.
 */
export function createFormGroup<T extends Record<string, FormValue>>(defaults: T): FormGroup {
  const controls = (Object.entries(defaults) as Array<[string, FormValue]>).reduce(
    (acc, [key, conf]): Record<string, FormControl> => {
      acc[key] = new FormControl(conf.value, conf.validators, conf.asyncValidators);
      return acc;
    },
    {} as Record<string, FormControl>,
  );

  return new FormGroup(controls);
}

/**
 * Filters a list of buttons by the user's rights.
 *
 * Behavior:
 * - If a button has no `visibilityRule` , it is kept.
 * - If a button has a `visibilityRule`, it is kept only if `userRights` is provided
 *   and `userRights[visibilityRule]` is truthy (ideally `true`).
 * - If `userRights` is omitted, buttons that specify a `visibilityRule` are filtered out.
 *
 * @param {TButton[]} buttons - Predefined buttons to evaluate.
 * @param {TRights} [userRights] - Optional lookup of user rights by rule name.
 * @returns {TButton[]} Buttons that are allowed to be shown for the given rights.
 */
export const getAllowedButtons = <
  TRights,
  TButton extends { visibilityRule?: PermissionKey<TRights> },
>(
  buttons: TButton[],
  userRights?: TRights,
): TButton[] =>
  buttons.filter(({ visibilityRule }): boolean =>
    visibilityRule == null ? true : !!(userRights && userRights[visibilityRule]),
  );

/**
 * RxJS operator that takes an emitted array of `Column` values, validates it,
 * and maps it to a tuple containing the original columns and a user-settings
 * action (add or edit) built from those columns.
 * @param {Observable<boolean>} selector -  An observable that emits whether default columns already exist (used to decide
 *   between creating or updating user settings). Only the first value is taken.
 * @param {string} key - The user-settings key under which the columns configuration should be stored/updated.
 * @returns {OperatorFunction<Column[], { updatedColumns: Column[]; settingsAction: AddUserSettings | EditUserSettings }>}
 *   An operator that, given a non-empty `Column[]`, emits an object with:
 *   `updatedColumns` and  `settingsAction`: an `AddUserSettings` or `EditUserSettings` action derived from the columns and `key`.
 */
export const transformColumns = (
  selector: Observable<boolean>,
  key: string,
): OperatorFunction<
  // A dismissed dialog emits `undefined`; the guard below already handles it,
  // so the input type says so rather than forcing callers to cast.
  Column[] | undefined,
  { settingsAction: AddUserSettings | EditUserSettings; updatedColumns: Column[] }
> =>
  switchMap(
    (
      updatedColumns?: Column[],
    ): Observable<{
      settingsAction: AddUserSettings | EditUserSettings;
      updatedColumns: Column[];
    }> => {
      const hasData = Array.isArray(updatedColumns) && updatedColumns.length > 0;
      if (!hasData) return EMPTY;

      const colsWithIndex = updatedColumns.map((col, index): Column => ({ ...col, index }));

      return selector.pipe(
        take(1),
        map(
          (
            hasDefaultColumns,
          ): { settingsAction: AddUserSettings | EditUserSettings; updatedColumns: Column[] } => ({
            settingsAction: buildSettingsAction(hasDefaultColumns, colsWithIndex, key),
            updatedColumns,
          }),
        ),
      );
    },
  );

/**
 * Computes the result of pasting a clipboard string into an input-like value.
 *
 * - Normalizes the original value (string or string[]) into a string
 * - Inserts formatted clipboard text at the current cursor selection
 * - Returns the updated value and the new cursor position
 */
const computePastedList = ({
  clipboardText,
  event,
  originValue,
}: PasteComputeInput): PasteComputeResult => {
  let originText = originValue ? originValue : '';
  if (Array.isArray(originText)) originText = originText.join(',');

  const start = event.selectionStart || 0;
  const end = event.selectionEnd || 0;
  // Fallback if Helper.formatPasteList doesn't exist
  const formatted = Helper.formatPasteList
    ? Helper.formatPasteList('', clipboardText)
    : clipboardText;

  const updatedValue = originText.substring(0, start) + formatted + originText.substring(end);
  return { clipboardText, newCursorPos: start + formatted.length, updatedValue };
};

/**
 * Reads text from the clipboard and applies it to an input element's value.
 *
 * - Supports dependency injection of the clipboard reader (for testing)
 * - Restores the cursor position after paste
 * - Returns the computed paste result
 */
export const pasteFromClipboard = async (
  event: HTMLInputElement,
  originValue: null | string | string[] | undefined,
  readText: () => Promise<string> = (): Promise<string> => navigator.clipboard.readText(),
): Promise<PasteComputeResult> => {
  try {
    const clipboardText = await readText();

    const result = computePastedList({
      clipboardText,
      event,
      originValue,
    });

    setTimeout((): void => {
      event.setSelectionRange(result.newCursorPos, result.newCursorPos);
    });

    return result;
  } catch (error) {
    console.error('Clipboard read failed:', error);

    // Hand back the value untouched. Computing with an empty clipboard would
    // "paste nothing over the selection" — deleting whatever the user had
    // highlighted, so a denied permission silently emptied the field.
    const originText = Array.isArray(originValue) ? originValue.join(',') : (originValue ?? '');

    return {
      clipboardText: '',
      newCursorPos: event.selectionStart ?? originText.length,
      updatedValue: originText,
    };
  }
};

/**
 * Retrieves a value from localStorage with a type-safe fallback.
 *
 * - Returns the fallback if the key does not exist
 * - Treats string fallbacks as raw values (no JSON parsing)
 * - Attempts JSON parsing for non-string fallbacks
 */
export const getLocalStorageItem = <T>(key: string, fallback: T): T => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;

  if (typeof fallback === 'string') {
    return raw as unknown as T;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

/**
 * Stores a value in localStorage.
 *
 * - Stores strings as-is
 * - Serializes non-string values as JSON
 */
export const setLocalStorageItem = <T>(key: string, value: T): void =>
  localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));

/**
 * Generates a numeric key representing the start of a given day (midnight).
 *
 * Useful for grouping or comparing dates by calendar day.
 */
/**
 * Creates an ascending date comparator function.
 *
 * @param getDate Function extracting a date value from an item
 * @returns Comparator function suitable for Array.prototype.sort
 */
export const sortByDateAsc =
  <T>(getDate: (item: T) => Date | number | string): ((a: T, b: T) => number) =>
  (a: T, b: T): number => {
    const da = new Date(getDate(a)).getTime();
    const db = new Date(getDate(b)).getTime();
    return da - db;
  };

/**
 * Maps a form-value object into a plain object by unwrapping `{ value }`.
 *
 * Example:
 * `{ field: { value: 123 } } → { field: 123 }`
 */
export const mapFormToObject = <T extends Record<string, FormValue>>(form: T): UnwrapValueForm<T> =>
  (Object.keys(form) as Array<keyof T>).reduce((acc, k): UnwrapValueForm<T> => {
    acc[k] = form[k].value;
    return acc;
  }, {} as UnwrapValueForm<T>);

// Re-exported from `@orion-platform/core` (`libs/core/src/lib/utils/`) so the
// canonical implementation lives in a place every app AND lib in the monorepo
// can import without violating the `app → lib` dependency direction. This
// alias keeps existing consumer imports from `apps/orion/.../utils/general.utils`
// working unchanged — new code SHOULD import directly from `libs/core/src/lib/utils`.
export { extractApiErrorMessage, isNetworkError, isObject, safeGet } from 'libs/core/src/lib/utils';
